// pairing.js — TamCentralStation PerformanceDataService.fetchPairedMoves, ported.
//
// The pairing / classification body below is copied VERBATIM from
// TamCentralStation src/services/PerformanceDataService.js (mainline). Only its
// I/O was replaced: events come from JarJar's YMSEV_<site>_<hour> cache instead
// of live getEventReport calls, location types + pending moves come from the
// YMSPERF_<site>_BOARD snapshot instead of the TCS store / listPendingMoves,
// and status/notify callbacks were removed. Do not edit the logic here; re-port
// from TCS instead so the numbers keep matching.
/* eslint-disable */
import { eventKey } from './eventKeys.js';
import { ANDON_EVENT_TYPES, ANDON_SUFFIXES, DOCK_EVENT_TYPES } from './TimingTable.js';
import { scopeAndonLifecyclesToWindow } from './andonLifecycle.js';

const BOARD_LOOKUP_MAX_AGE_MS = 6 * 3600000;

/**
 * Compute the set of HOSTLER_CREATE eventIds that represent shuttle moves.
 *
 * A move is a shuttle if EITHER endpoint is offsite, matching the pair-based _hasOffsite
 * semantics used for shuttle completes and timing. The two directions resolve differently
 * because a hostler event's `location` is always the DESTINATION, never the origin:
 *   - main→offsite: destination is offsite → known directly from create.location.
 *   - offsite→main: destination is the main site → origin must come from the start or board.
 *
 * Per create, shuttle if any of:
 *   0. create.location (destination) is offsite → covers main→offsite immediately.
 *   1. Matched start exists (paired OR in-flight) and start.location (origin) is offsite →
 *      covers offsite→main. Ground truth; also handles started-but-not-completed moves.
 *   2. No start, create is recent (≤ BOARD_LOOKUP_MAX_AGE_MS old) AND the live move board
 *      has a pending move for this create (matched by visitId, fallback vehicleNumber) whose
 *      sourceLocation.code is offsite → covers offsite→main before the move starts.
 *      Best-effort; bounded to recent unexecuted creates so stale historical creates never
 *      match today's board.
 *   3. Otherwise → in-yard (origin unknown, destination onsite).
 *
 * Note: sourceLocation.type is not populated on board records — resolve the code against
 * the location type map via isOffsite(code).
 *
 * @param {Object[]} createEvents - HOSTLER_CREATE events (destination location on each)
 * @param {Object[]} startEvents - HOSTLER_START events (origin location on each)
 * @param {Object[]} pendingMovements - Live board movements ({ sourceLocation, yardAssets })
 * @param {(code: string) => boolean} isOffsite - Resolves a location code to offsite-ness
 * @param {number} nowMs - Current time in ms (injectable for testing)
 * @returns {Set<string>} eventIds of creates classified as shuttle
 */
function computeShuttleCreateIds(createEvents, startEvents, pendingMovements, isOffsite, nowMs = Date.now()) {
    const shuttleCreateIds = new Set();

    // Board origin lookup maps: visitId → sourceLocation.code, vehicleNumber → sourceLocation.code.
    // visitId is preferred (per-visit), vehicleNumber is the fallback when a create lacks a visitId.
    const boardSourceByVisit = new Map();
    const boardSourceByVehicle = new Map();
    for (const m of pendingMovements || []) {
        const src = m?.sourceLocation?.code;
        if (!src) continue;
        const asset = (m.yardAssets || [])[0];
        if (!asset) continue;
        if (asset.visitId != null) boardSourceByVisit.set(String(asset.visitId), src);
        if (asset.vehicleNumber != null) boardSourceByVehicle.set(String(asset.vehicleNumber), src);
    }

    // Track consumed starts so two creates for the same vehicle don't both claim one start.
    const usedStartIds = new Set();

    for (const create of createEvents) {
        // A move is a shuttle if EITHER endpoint is offsite (matching the pair-based
        // _hasOffsite semantics used for shuttle completes/timing). The create's own
        // location is the DESTINATION, so it directly covers the main→offsite direction.
        if (isOffsite(create.location)) {
            shuttleCreateIds.add(create.eventId);
            // Still consume a matching start below so vehicle-level start accounting stays
            // consistent, but classification is already decided.
        }

        // Origin side (offsite→main): needs a start or the live board, since the create
        // destination is the main site in that direction.
        // Tier 1: earliest unused start for this vehicle at/after the create.
        const start = startEvents.find(s =>
            !usedStartIds.has(s.eventId) &&
            s.vehicleNumber === create.vehicleNumber &&
            s.timestamp >= create.timestamp
        );
        if (start) {
            usedStartIds.add(start.eventId);
            if (isOffsite(start.location)) shuttleCreateIds.add(create.eventId);
            continue;
        }

        // Tier 2: no start — consult the live board, but only for recent creates.
        const ageMs = nowMs - create.timestamp * 1000;
        if (ageMs > BOARD_LOOKUP_MAX_AGE_MS) continue;

        const src = (create.visitId != null && boardSourceByVisit.get(String(create.visitId)))
            || (create.vehicleNumber != null && boardSourceByVehicle.get(String(create.vehicleNumber)))
            || null;
        if (src && isOffsite(src)) shuttleCreateIds.add(create.eventId);
        // Tier 3: unresolved → in-yard default (not added unless destination was offsite above).
    }

    return shuttleCreateIds;
}

/**
 * Build a read-only per-create move-detail map from the pairing state the
 * pairing loop already produced. Pure derivation of already-fetched data — no
 * new fetch, no mutation of pairedMoves/cancelledCreateIds/startEvents.
 *
 * All timestamps throughout are in SECONDS (never multiply by 1000 here; the
 * view's fmt/fmtTime handle the ×1000 when rendering).
 *
 * @param {Object[]} createEvents  - in-window HOSTLER_CREATE events (which creates get a ROW)
 * @param {Object[]} allCreateEvents - UNscoped HOSTLER_CREATE events (incl. pre-shift);
 *                                     the set the cancel/checkout loops associate against
 * @param {Set<string>} cancelledCreateIds - explicit + implicit cancels (final)
 * @param {Map<string,Object>} startByCreateId - create.eventId → the HOSTLER_START the
 *                                     pairing loop consumed for it (recorded at match time).
 *                                     Supplies the in-progress row's start/from/vehicle/user.
 * @param {Set<string>} inProgressCreateIds - create ids the PAIRING LOOP decided are in
 *                                     progress AT its decision point (matched a start, no
 *                                     attributable complete, no terminal complete for the
 *                                     vehicle), already demoted by the RELEASE/stale passes.
 *                                     This is authoritative — the classifier does NOT
 *                                     re-derive in-progress from residual start/absorbed sets
 *                                     (that reconstruction was the recurring defect).
 * @param {Array<{create,start,complete}>} pairedMoves - final paired moves
 * @param {Object[]} cancelEvents  - raw HOSTLER_CANCEL events (for cancelTime)
 * @returns {Map<string, MoveDetail>} keyed by create.eventId, one entry per createEvents row
 *
 * @typedef {Object} MoveDetail
 * @property {'completed'|'inProgress'|'pending'|'cancelled'} status
 * @property {Object} [start]        resolved HOSTLER_START event (absent on cancelled/pending)
 * @property {Object} [complete]     resolved HOSTLER_COMPLETE event (absent unless completed)
 * @property {number} [cancelTime]   cancel ts (seconds), explicit cancel only
 * @property {number} [duration]     complete.timestamp - start.timestamp (secs)
 * @property {string} [fromLocation] start.location
 * @property {string} [toLocation]   destination; create.location for every create-side row,
 *                                   overridden by complete.location once completed
 * @property {string} [vehicleNumber]
 * @property {string} [userId]
 */
export function buildMoveDetailByCreateId(
    createEvents, allCreateEvents, cancelledCreateIds, startByCreateId, inProgressCreateIds, pairedMoves, cancelEvents,
) {
    // 1. Index pairedMoves by create.eventId (skip half-measure Start→Complete pairs).
    const pairedByCreateId = new Map();
    for (const p of (pairedMoves || [])) {
        if (p.create) pairedByCreateId.set(p.create.eventId, p);
    }

    // 2. Mirror the real cancel loop EXACTLY: associate each HOSTLER_CANCEL against
    //    allCreateEvents (unscoped), picking the latest create with the same
    //    vehicleNumber whose timestamp <= cancel.timestamp.
    const explicitCancelTimeByCreateId = new Map();
    for (const cancel of (cancelEvents || [])) {
        const target = [...(allCreateEvents || [])]
            .filter(c => c.vehicleNumber === cancel.vehicleNumber && c.timestamp <= cancel.timestamp)
            .sort((a, b) => b.timestamp - a.timestamp)[0];
        if (target) explicitCancelTimeByCreateId.set(target.eventId, cancel.timestamp);
    }

    // 3. Classify each in-window create with precedence cancelled → completed → inProgress → pending.
    //    toLocation is the known destination (create.location) for every create-side row; the
    //    completed branch overrides it with the actual complete.location (F5).
    const out = new Map();
    for (const create of (createEvents || [])) {
        if (cancelledCreateIds?.has(create.eventId)) {
            out.set(create.eventId, {
                status: 'cancelled',
                cancelTime: explicitCancelTimeByCreateId.get(create.eventId) ?? undefined,
                toLocation: create.location,
                vehicleNumber: create.vehicleNumber,
                userId: create.userId,
            });
            continue;
        }

        const pair = pairedByCreateId.get(create.eventId);
        if (pair) {
            const { start, complete } = pair;
            out.set(create.eventId, {
                status: 'completed',
                start,
                complete,
                duration: complete.timestamp - start.timestamp,
                fromLocation: start.location,
                toLocation: complete.location,
                vehicleNumber: start.vehicleNumber ?? create.vehicleNumber,
                userId: complete.userId ?? start.userId,
            });
            continue;
        }

        // inProgress: membership is decided authoritatively by the pairing loop (and the
        // RELEASE/stale demotions) — this classifier only READS the set, it does not
        // reconstruct the decision. startByCreateId supplies the row's start detail.
        if (inProgressCreateIds?.has(create.eventId)) {
            const inProgressStart = startByCreateId?.get(create.eventId);
            out.set(create.eventId, {
                status: 'inProgress',
                start: inProgressStart,
                fromLocation: inProgressStart?.location,
                toLocation: create.location,
                vehicleNumber: inProgressStart?.vehicleNumber ?? create.vehicleNumber,
                userId: inProgressStart?.userId ?? create.userId,
            });
            continue;
        }

        out.set(create.eventId, {
            status: 'pending',
            toLocation: create.location,
            vehicleNumber: create.vehicleNumber,
            userId: create.userId,
        });
    }

    return out;
}


/**
 * @param {object} p
 * @param {object[]} p.events          all events in [fromDate-2h, toDate+2h)
 * @param {object[]} p.lookbackEvents  events in [fromDate-6h, fromDate-2h)
 * @param {object}   p.board           { locationTypeMap, pendingMovements }
 */
export function computePairedMoves({ yard, dateVal, startHour, endHour, fromDate, toDate, events, lookbackEvents, board, nowMs = Date.now() }) {
        const fromDatePadded = fromDate - 2 * 3600000;
        const toDatePadded  = toDate + 2 * 3600000;
        const hostlerEvents     = events;

        // ─── Local categorization of unfiltered events ───
        const KNOWN_CATEGORIES = {
            hostler_create: new Set(['HOSTLER_CREATE']),
            hostler_start: new Set(['HOSTLER_START']),
            hostler_move: new Set(['MOVE_BY_HOSTLER']),
            hostler_complete: new Set(['HOSTLER_COMPLETE']),
            hostler_cancel: new Set(['HOSTLER_CANCEL']),
            hostler_release: new Set(['HOSTLER_RELEASE']),
            check_in: new Set(['CHECK_IN']),
            check_out: new Set(['CHECK_OUT']),
            dock: new Set(['TDR_DOCK', 'TDR_RELEASE']),
            detach: new Set(['DETACH_HAUL_ACTION']),
            location_audit: new Set(['LOCATION_AUDIT']),
        };
        const KNOWN_TYPES = new Set(Object.values(KNOWN_CATEGORIES).flatMap(s => [...s]));
        // Andon types match CREATE_*, START_*, COMPLETE_*, CANCEL_*, ABANDON_* prefix pattern
        for (const t of ANDON_EVENT_TYPES) KNOWN_TYPES.add(t);
        // Intentionally ignored types — not relevant to performance processing.
        // CANCEL_*/ABANDON_* andon types are NOT listed here: they are known andon
        // events covered by ANDON_EVENT_TYPES → KNOWN_TYPES above.
        const EXCLUDED_TYPES = new Set([
            'ADD_EQUIPMENT', 'ATTACH_HAUL_ACTION', 'OB_DOCK_COMPLETED', 'OB_DOCK_STARTED',
            'REJECT_CHECK_IN', 'REMOVE_LOAD_INTEGRITY', 'UPDATE_EQUIPMENT',
            'UPDATE_EQUIPMENT_EMPTY_STATUS', 'UPDATE_EQUIPMENT_TYPE', 'UPDATE_HAUL_ACTION',
            'UPDATE_LICENSE_PLATE', 'UPDATE_NOTES', 'UPDATE_SEALS',
            'ADD_LOAD_INTEGRITY', 'CHECKOUT_REJECTION_RETURN_TIMEOUT', 'CORRECTION_ADDED',
            'CORRECTION_DISPLACED', 'CORRECTION_LOCATION', 'CORRECTION_REMOVED', 'IB_DOCK_COMPLETED',
            'IB_DOCK_STARTED', 'MOVE_BY_TRACTOR', 'MOVE_SELF', 'PREREGISTRATION', 'REJECT_CHECK_OUT', 'REMOVE_EQUIPMENT',
            'RE_CHECK_IN_FOR_REJECTED_CHECK_OUT', 'UPDATE_EQUIPMENT_CONDITION',
            'UPDATE_EQUIPMENT_REJECTION_DETAILS', 'UPDATE_LOAD_INTEGRITY', 'UPDATE_OPERATOR', 'UPDATE_OWNER',
        ]);

        const uncategorizedTypes = new Map();
        for (const e of hostlerEvents) {
            const t = e.eventType;
            if (!t) continue;
            if (KNOWN_TYPES.has(t) || EXCLUDED_TYPES.has(t)) continue;
            uncategorizedTypes.set(t, (uncategorizedTypes.get(t) || 0) + 1);
        }
        if (uncategorizedTypes.size) {
            console.warn(`[PDS] ⚠️ Uncategorized event types:`, Object.fromEntries(uncategorizedTypes));
        }

        // ─── Filter andon events from the unfiltered fetch ───
        const andonEvents = hostlerEvents.filter(e => ANDON_EVENT_TYPES.includes(e.eventType) && e.timestamp * 1000 >= fromDate && e.timestamp * 1000 < toDate);
        // Resolve andon lifecycles once (shared order-independent resolver) for all duration consumers.
        // Completions resolve over the ±2h padded fetch, then scope by COMPLETE time, so an andon
        // started before the window but completed inside it keeps its duration. andonEvents stays
        // window-scoped, so heatmaps and counts are unaffected.
        const paddedAndonEvents = hostlerEvents.filter(e => ANDON_EVENT_TYPES.includes(e.eventType) && e.timestamp * 1000 >= fromDatePadded && e.timestamp * 1000 < toDatePadded);
        const andonLifecycles = scopeAndonLifecyclesToWindow(paddedAndonEvents, andonEvents, fromDate, toDate, ANDON_SUFFIXES);

        // createEvents and completeEvents are scoped to the shift window for display/counts
        // startEvents uses the full padded window so pre-shift starts can anchor their completes
        const createEvents      = hostlerEvents.filter(e => e.eventType === 'HOSTLER_CREATE'  && e.timestamp * 1000 >= fromDate && e.timestamp * 1000 < toDate);
        const startEvents = (() => {
            const sorted = hostlerEvents.filter(e => e.eventType === 'HOSTLER_START').sort((a, b) => a.timestamp - b.timestamp);
            const allCompletes = hostlerEvents.filter(e => (e.eventType === 'MOVE_BY_HOSTLER' || e.eventType === 'HOSTLER_COMPLETE') && e.location !== 'IN_TRANSIT');
            // Drop any start that is superseded by a later start on the same vehicle with no complete in between
            return sorted.filter((s, i) => {
                const nextStart = sorted.slice(i + 1).find(n => n.vehicleNumber === s.vehicleNumber);
                if (!nextStart) return true;
                const completeBetween = allCompletes.find(c =>
                    c.vehicleNumber === s.vehicleNumber &&
                    c.timestamp > s.timestamp &&
                    c.timestamp < nextStart.timestamp
                );
                return !!completeBetween;
            });
        })();
        // completeEvents: HOSTLER_COMPLETE scoped to shift window for display/heatmaps
        // allCompleteEvents: HOSTLER_COMPLETE with a real destination (non-IN_TRANSIT) for pairing
        // MOVE_BY_HOSTLER events are intermediate location updates, not terminal completions.
        // Scoped to fromDate onwards (excludes lookback period). The lookback only provides
        // creates/starts that anchor their completes into the main window — we don't want to
        // pair completes that happened entirely before the window.
        const allCompleteEvents = hostlerEvents.filter(e =>
            e.eventType === 'HOSTLER_COMPLETE' &&
            e.location !== 'IN_TRANSIT' &&
            e.timestamp * 1000 >= fromDate &&
            e.timestamp * 1000 < toDate
        );
        // completeEvents: same set used for heatmaps (only HOSTLER_COMPLETE)
        const completeEvents = allCompleteEvents;
        const dockEvents        = hostlerEvents.filter(e => DOCK_EVENT_TYPES.includes(e.eventType) || DOCK_EVENT_TYPES.includes(e.eventDescription));
        for (const e of dockEvents) { if (!e.eventType && e.eventDescription) e.eventType = e.eventDescription; }

        // ─── Extended lookback fetch (6h before main window) ───
        // Covers the gap between fromDate-6h and fromDatePadded (fromDate-2h).
        // Used for: detach events (wider window) and prior-window pairs (dock timing).
        // Events within the main window are already in hostlerEvents — no duplication.
        
        let extendedEvents = [];
        try {
            const extendedFrom = fromDate - 6 * 3600000;
            // Only fetch the gap not already covered by the main fetch
            if (extendedFrom < fromDatePadded) {
                // JarJar: lookback events come from the same hour-block cache (no extra fetch).
                extendedEvents = (lookbackEvents || []).filter(e => {
                    const tsMs = e.timestamp * 1000;
                    return tsMs >= extendedFrom && tsMs < fromDatePadded;
                });
            }
        } catch (e) {
            console.warn('[PDS] ⚠️ Extended lookback fetch failed:', e.message);
        }

        // Extract detach events from both main + extended windows
        const allDetachCandidates = [...hostlerEvents, ...extendedEvents];
        const detachEvents = allDetachCandidates.filter(e =>
            e.eventType === 'DETACH_HAUL_ACTION' || e.eventDescription === 'DETACH_HAUL_ACTION'
        );
        for (const e of detachEvents) e.eventType = 'DETACH_HAUL_ACTION';
        // Merge detach into dockEvents, deduplicating by composite eventKey (main + extended
        // windows overlap, so this drops true duplicates; eventId alone could drop a distinct
        // detach that happens to share an eventId with another event).
        const existingDockIds = new Set(dockEvents.map(e => eventKey(e)));
        for (const e of detachEvents) {
            if (!existingDockIds.has(eventKey(e))) { existingDockIds.add(eventKey(e)); dockEvents.push(e); }
        }
        if (detachEvents.length) console.log(`[PDS] ${detachEvents.length} DETACH_HAUL_ACTION event(s) found.`);

        // Load signal for the Detach→Create metric. UPDATE_EQUIPMENT_EMPTY_STATUS between a
        // detach and its create means the trailer was (re)loaded after detaching, so the
        // subsequent create is "move a freshly-loaded trailer" — NOT the detach-and-left-
        // sitting case that metric is meant to measure. Carried in dockEvents so
        // buildDockTimingRows can exclude those pairs. Uses the same wide (main + extended)
        // window as detach so a load just before the window start is still visible.
        const loadStatusEvents = allDetachCandidates.filter(e => e.eventType === 'UPDATE_EQUIPMENT_EMPTY_STATUS');
        for (const e of loadStatusEvents) {
            if (!existingDockIds.has(eventKey(e))) { existingDockIds.add(eventKey(e)); dockEvents.push(e); }
        }
        if (loadStatusEvents.length) console.log(`[PDS] ${loadStatusEvents.length} UPDATE_EQUIPMENT_EMPTY_STATUS event(s) found.`);

        
        // Include pre-shift creates in pairing pool so they can consume their completes and free up in-window completes
        const allCreateEvents   = hostlerEvents.filter(e => e.eventType === 'HOSTLER_CREATE');
        const cancelEvents = hostlerEvents.filter(e => e.eventType === 'HOSTLER_CANCEL');
        const cancelledCreateIds = new Set();
        for (const cancel of cancelEvents) {
            const target = [...allCreateEvents]
                .filter(c => c.vehicleNumber === cancel.vehicleNumber && c.timestamp <= cancel.timestamp)
                .sort((a, b) => b.timestamp - a.timestamp)[0];
            if (target) cancelledCreateIds.add(target.eventId);
        }

        // Implicit cancellation / consumption via CHECK_OUT:
        // - If no HOSTLER_START between create and checkout → move was never executed (implicit cancel)
        // - If a start exists but no complete before checkout → asset departed mid-move (effectively consumed)
        const allCheckOutEvents = hostlerEvents.filter(e => e.eventType === 'CHECK_OUT');
        const checkoutConsumedStartIds = new Set();
        for (const co of allCheckOutEvents) {
            const target = [...allCreateEvents]
                .filter(c => c.vehicleNumber === co.vehicleNumber && c.timestamp <= co.timestamp)
                .sort((a, b) => b.timestamp - a.timestamp)[0];
            if (!target) continue;
            if (cancelledCreateIds.has(target.eventId)) continue;
            const matchedStart = startEvents.find(s =>
                s.vehicleNumber === co.vehicleNumber &&
                s.timestamp >= target.timestamp &&
                s.timestamp <= co.timestamp
            );
            if (!matchedStart) {
                // No start → implicit cancel (move was never executed)
                cancelledCreateIds.add(target.eventId);
            } else {
                // Start exists but vehicle checked out before complete → effectively consumed
                const hasComplete = allCompleteEvents.some(c =>
                    c.vehicleNumber === co.vehicleNumber &&
                    c.timestamp >= matchedStart.timestamp &&
                    c.timestamp <= co.timestamp
                );
                if (!hasComplete) {
                    cancelledCreateIds.add(target.eventId);
                    checkoutConsumedStartIds.add(matchedStart.eventId);
                }
            }
        }
        if (checkoutConsumedStartIds.size) console.log(`[PDS] ${checkoutConsumedStartIds.size} start(s) consumed by CHECK_OUT (asset departed mid-move).`);

        const locationTypeMap = board?.locationTypeMap || {};
        const isOffsite = code => (locationTypeMap[code] || '').toUpperCase() === 'OFFSITELOCATION';

        const pairedMoves   = [];
        const usedStarts    = new Set(checkoutConsumedStartIds);
        const usedCompletes = new Set();
        // Records the create→start association the moment the pairing loop consumes a start,
        // BEFORE it is known whether a COMPLETE follows. A create that matches a start but
        // never pairs to a complete is a genuine in-progress move; its start is already in
        // usedStarts (consumed), so inProgress classification cannot rely on usedStarts alone.
        // This map lets buildMoveDetailByCreateId recover the in-progress start (F1).
        const startByCreateId = new Map();
        // inProgress is decided HERE, at the loop's decision point — not reconstructed
        // afterward from usedStarts/startByCreateId minus the absorption sets (that
        // reconstruction was the recurring defect: a consumed-but-unpaired start has
        // several distinct causes, and subtracting them after the fact kept missing one).
        // A create is genuinely in-progress iff: it matched a start, that start did NOT
        // pair to a complete, AND no terminal COMPLETE exists for the vehicle at/after the
        // start under the SAME geometry the pairing policy uses (same-user allowance +
        // no intervening start). If such a complete exists but pairing rejected it for
        // attribution reasons, the trailer still physically moved → the move EXECUTED, so
        // the create is not in-progress (it falls through to pending below). RELEASE/stale
        // absorption (which runs after this loop) removes any of these starts from
        // inProgressCreateIds so a dropped/dead-thread move also classifies pending.
        const inProgressCreateIds = new Set();
        const createsSorted = [...allCreateEvents].sort((a, b) => a.timestamp - b.timestamp);

        for (const create of createsSorted) {
            if (cancelledCreateIds.has(create.eventId)) continue;
            const start = startEvents.find(s =>
                !usedStarts.has(s.eventId) &&
                s.vehicleNumber === create.vehicleNumber &&
                s.timestamp >= create.timestamp
            );
            if (!start) continue;
            usedStarts.add(start.eventId);
            startByCreateId.set(create.eventId, start);
            // Candidate complete under the pairing policy (same user, no intervening start).
            const complete = allCompleteEvents
                .filter(c => {
                    if (usedCompletes.has(c.eventId)) return false;
                    if (c.vehicleNumber !== start.vehicleNumber) return false;
                    if (c.timestamp < start.timestamp) return false;
                    // Start and complete must share the same user
                    if (c.userId && start.userId && c.userId !== start.userId) return false;
                    // Disqualify if another start for the same vehicle falls between this start and the complete
                    const interveningStart = startEvents.find(s =>
                        s.eventId !== start.eventId &&
                        s.vehicleNumber === start.vehicleNumber &&
                        s.timestamp > start.timestamp &&
                        s.timestamp < c.timestamp
                    );
                    return !interveningStart;
                })
                .sort((a, b) => a.timestamp - b.timestamp)[0];
            if (!complete) {
                // No attributable complete. Decide in-progress vs executed-but-unpaired HERE:
                // does ANY terminal complete exist for this vehicle at/after the start with no
                // intervening start (i.e. the move physically finished, just not attributable)?
                // If so, the move executed → leave the create to fall through to pending. If
                // not, the trailer never reached a destination → genuinely in-progress.
                const anyTerminalComplete = allCompleteEvents.some(c => {
                    if (c.vehicleNumber !== start.vehicleNumber) return false;
                    if (c.timestamp < start.timestamp) return false;
                    const interveningStart = startEvents.find(s =>
                        s.eventId !== start.eventId &&
                        s.vehicleNumber === start.vehicleNumber &&
                        s.timestamp > start.timestamp &&
                        s.timestamp < c.timestamp
                    );
                    return !interveningStart;
                });
                if (!anyTerminalComplete) inProgressCreateIds.add(create.eventId);
                continue;
            }
            usedCompletes.add(complete.eventId);
            pairedMoves.push({ create, start, complete });
        }

        // ─── Half-measure pairing: Start → Complete for orphaned events ───
        // Only runs after all Create-anchored pairs are exhausted. Handles moves where
        // the Create is outside the fetch window (long-dwelling trailers).
        const orphanStarts = startEvents.filter(s => !usedStarts.has(s.eventId));
        for (const start of orphanStarts.sort((a, b) => a.timestamp - b.timestamp)) {
            const complete = allCompleteEvents
                .filter(c => {
                    if (usedCompletes.has(c.eventId)) return false;
                    if (c.vehicleNumber !== start.vehicleNumber) return false;
                    if (c.timestamp < start.timestamp) return false;
                    if (c.userId && start.userId && c.userId !== start.userId) return false;
                    const interveningStart = startEvents.find(s =>
                        s.eventId !== start.eventId &&
                        s.vehicleNumber === start.vehicleNumber &&
                        s.timestamp > start.timestamp &&
                        s.timestamp < c.timestamp
                    );
                    return !interveningStart;
                })
                .sort((a, b) => a.timestamp - b.timestamp)[0];
            if (!complete) continue;
            usedStarts.add(start.eventId);
            usedCompletes.add(complete.eventId);
            pairedMoves.push({ create: null, start, complete });
        }
        const halfMeasureCount = pairedMoves.filter(p => p.create === null).length;
        if (halfMeasureCount) console.log(`[PDS] ${halfMeasureCount} move(s) paired via Start→Complete fallback.`);

        // ─── HOSTLER_RELEASE absorption: mark starts as consumed if released ───
        // A HOSTLER_RELEASE means the driver grabbed the move then dropped it (dead thread).
        // The start is consumed but produces no paired move.
        const releaseEvents = hostlerEvents.filter(e => e.eventType === 'HOSTLER_RELEASE');
        // Starts absorbed by RELEASE/stale-visitId are tracked separately so inProgress
        // classification can distinguish a genuinely in-progress start (consumed by the
        // pairing match but never completed) from a dead-thread/stale start (F1). These
        // absorbed starts belong to no paired move and must NOT surface as in-progress.
        const absorbedStartIds = new Set();
        // Reverse index: start.eventId → create.eventId, so a start absorbed below can
        // demote its create out of inProgressCreateIds (a released/stale start means the
        // move was dropped, not still running). Built from the loop's own associations.
        const createIdByStartId = new Map();
        for (const [createId, s] of startByCreateId) createIdByStartId.set(s.eventId, createId);
        const demoteStartFromInProgress = startId => {
            const createId = createIdByStartId.get(startId);
            if (createId) inProgressCreateIds.delete(createId);
        };
        let releasedStartCount = 0;
        for (const release of releaseEvents) {
            const orphanStart = startEvents.find(s =>
                !usedStarts.has(s.eventId) &&
                s.vehicleNumber === release.vehicleNumber &&
                s.timestamp <= release.timestamp
            );
            if (orphanStart) {
                usedStarts.add(orphanStart.eventId);
                absorbedStartIds.add(orphanStart.eventId);
                demoteStartFromInProgress(orphanStart.eventId);
                releasedStartCount++;
                continue;
            }
            // A create→start→RELEASE flow: the main loop already CONSUMED the start (so the
            // orphan scan above, which only sees unconsumed starts, can't catch it) and left
            // it unpaired → it is currently an inProgress candidate. The RELEASE means the
            // driver dropped the move, so demote that create to pending. Only an unpaired,
            // not-yet-absorbed consumed start for this vehicle at/before the release qualifies;
            // completed pairs' starts are never in inProgressCreateIds, so they're untouched.
            const consumedInProgressStart = startEvents.find(s =>
                s.vehicleNumber === release.vehicleNumber &&
                s.timestamp <= release.timestamp &&
                !absorbedStartIds.has(s.eventId) &&
                inProgressCreateIds.has(createIdByStartId.get(s.eventId))
            );
            if (consumedInProgressStart) {
                absorbedStartIds.add(consumedInProgressStart.eventId);
                demoteStartFromInProgress(consumedInProgressStart.eventId);
                releasedStartCount++;
            }
        }
        if (releasedStartCount) console.log(`[PDS] ${releasedStartCount} start(s) absorbed by HOSTLER_RELEASE (dead threads).`);

        // ─── Stale start absorption: mark starts as consumed if their visitId ───
        // already has a completed move (by any driver). These are location corrections
        // or reassignment events, not real active moves.
        const pairedVisitIds = new Set(pairedMoves.map(p => p.complete.visitId).filter(Boolean));
        let staleStartCount = 0;
        for (const start of startEvents) {
            if (usedStarts.has(start.eventId)) continue;
            if (start.visitId && pairedVisitIds.has(start.visitId)) {
                usedStarts.add(start.eventId);
                absorbedStartIds.add(start.eventId);
                demoteStartFromInProgress(start.eventId);
                staleStartCount++;
            }
        }
        if (staleStartCount) console.log(`[PDS] ${staleStartCount} start(s) absorbed: visitId already has a completed move.`);

        const inWindowPairs  = pairedMoves.filter(p => p.complete.timestamp * 1000 >= fromDate && p.complete.timestamp * 1000 < toDate);

        const _hasOffsite = p => isOffsite(p.create?.location) || isOffsite(p.start.location) || isOffsite(p.complete.location);
        const onsitePairs   = inWindowPairs.filter(p => !_hasOffsite(p));
        const offsitePairs  = inWindowPairs.filter(p =>  _hasOffsite(p));
        if (pairedMoves.length !== inWindowPairs.length)
            console.log(`[PDS] ${pairedMoves.length - inWindowPairs.length} paired move(s) excluded (complete outside shift window).`);

        // ─── Prior-window pairs for dock timing lookback ───
        // Uses extendedEvents (already fetched above) — no additional API call needed.
        
        let priorPairs = [];
        try {
            const priorFrom = fromDate - 6 * 3600000;
            const priorEvents = extendedEvents.filter(e => {
                const tsMs = e.timestamp * 1000;
                return tsMs >= priorFrom && tsMs < fromDate;
            });
            const priorCreates   = priorEvents.filter(e => e.eventType === 'HOSTLER_CREATE').sort((a, b) => a.timestamp - b.timestamp);
            const priorStarts    = priorEvents.filter(e => e.eventType === 'HOSTLER_START').sort((a, b) => a.timestamp - b.timestamp);
            const priorCompletes = priorEvents.filter(e => e.eventType === 'HOSTLER_COMPLETE' && e.location !== 'IN_TRANSIT').sort((a, b) => a.timestamp - b.timestamp);
            const priorCancels   = priorEvents.filter(e => e.eventType === 'HOSTLER_CANCEL');
            const priorCancelledIds = new Set();
            for (const cancel of priorCancels) {
                const target = priorCreates.filter(c => c.vehicleNumber === cancel.vehicleNumber && c.timestamp <= cancel.timestamp).at(-1);
                if (target) priorCancelledIds.add(target.eventId);
            }
            // Dedup starts: drop superseded starts with no complete in between
            const validPriorStarts = priorStarts.filter((s, i) => {
                const nextStart = priorStarts.slice(i + 1).find(n => n.vehicleNumber === s.vehicleNumber);
                if (!nextStart) return true;
                return priorCompletes.some(c => c.vehicleNumber === s.vehicleNumber && c.timestamp > s.timestamp && c.timestamp < nextStart.timestamp);
            });
            const usedPriorStarts = new Set();
            const usedPriorCompletes = new Set();
            for (const create of priorCreates) {
                if (priorCancelledIds.has(create.eventId)) continue;
                const start = validPriorStarts.find(s => !usedPriorStarts.has(s.eventId) && s.vehicleNumber === create.vehicleNumber && s.timestamp >= create.timestamp);
                if (!start) continue;
                usedPriorStarts.add(start.eventId);
                const complete = priorCompletes.filter(c => {
                    if (usedPriorCompletes.has(c.eventId)) return false;
                    if (c.vehicleNumber !== start.vehicleNumber || c.timestamp < start.timestamp) return false;
                    if (c.userId && start.userId && c.userId !== start.userId) return false;
                    return !validPriorStarts.some(s2 => s2.eventId !== start.eventId && s2.vehicleNumber === start.vehicleNumber && s2.timestamp > start.timestamp && s2.timestamp < c.timestamp);
                }).sort((a, b) => a.timestamp - b.timestamp)[0];
                if (!complete) continue;
                usedPriorCompletes.add(complete.eventId);
                priorPairs.push({ create, start, complete });
            }
            console.log(`[PDS] ${priorPairs.length} prior-window pair(s) fetched for dock timing.`);
        } catch (e) {
            console.warn('[PDS] ⚠️ Prior-window fetch failed, dock timing for early hours may be incomplete:', e.message);
        }

        const checkInEvents  = hostlerEvents.filter(e => e.eventType === 'CHECK_IN'  && e.timestamp * 1000 >= fromDate && e.timestamp * 1000 < toDate);
        const checkOutEvents = hostlerEvents.filter(e => e.eventType === 'CHECK_OUT' && e.timestamp * 1000 >= fromDate && e.timestamp * 1000 < toDate);

        // ─── Fetch pending moves to identify offsite-target vehicles ───
        let offsiteTargetVehicles = new Set();
        let pendingMovements = [];
        try {
            const pendingRaw = { movements: board?.pendingMovements || [] };
            pendingMovements = pendingRaw?.movements || [];
            for (const m of pendingMovements) {
                const targetCode = m.targetLocation?.code;
                if (targetCode && isOffsite(targetCode)) {
                    const vn = m.yardAssets?.[0]?.vehicleNumber;
                    if (vn) offsiteTargetVehicles.add(vn);
                }
            }
            if (offsiteTargetVehicles.size) console.log(`[PDS] ${offsiteTargetVehicles.size} pending move(s) with offsite target.`);
        } catch (e) {
            console.warn('[PDS] ⚠️ Pending moves fetch failed, shuttle detection for unpaired creates may be incomplete:', e.message);
        }

        // Classify each create by origin so offsite→main shuttle creates surface in the
        // shuttle section (start.location for started moves; live board sourceLocation for
        // recent unstarted creates). Complements pair-based classification, which only knows
        // origin once a move has a start.
        const shuttleCreateIds = computeShuttleCreateIds(createEvents, startEvents, pendingMovements, isOffsite);
        if (shuttleCreateIds.size) console.log(`[PDS] ${shuttleCreateIds.size} create(s) classified as shuttle.`);

        // Per-create move-execution detail for the heatmap cell-click modal. Built AFTER the
        // HOSTLER_RELEASE and stale-visitId absorptions so usedStarts is final (a release/stale-
        // absorbed start must classify its create pending, not inProgress). Pure read-only
        // derivation of the already-computed pairing state — no new fetch, no mutation.
        const moveDetailByCreateId = buildMoveDetailByCreateId(
            createEvents, allCreateEvents, cancelledCreateIds, startByCreateId, inProgressCreateIds, pairedMoves, cancelEvents,
        );

                const result = {
            yard, dateVal, startHour, endHour, fromDate, toDate, numHours: Math.round((toDate - fromDate) / 3600000), fetchedAt: nowMs,
            createEvents, startEvents, completeEvents, dockEvents, cancelledCreateIds, andonEvents: [], andonLifecycles: [],
            checkInEvents, checkOutEvents, moveDetailByCreateId,
            pairedMoves, onsitePairs, offsitePairs, priorPairs, isOffsite, offsiteTargetVehicles, shuttleCreateIds, phase: 'moves',
            _rawEvents: hostlerEvents, uncategorizedTypes: Object.fromEntries(uncategorizedTypes),
            _usedStarts: usedStarts, _usedCompletes: usedCompletes,
        };
        result.andonEvents = andonEvents;
        result.andonLifecycles = andonLifecycles;
        result.phase = 'complete';
        return result;
}
